import { isDefined, isNotDefined } from '@sgme/fp';
import { type ChangeEvent, useContext } from 'react';
import { Form } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';

import { useTrackEvent } from '@/App/Blotter/useTrackEvent.ts';
import { OrderRow } from '@/App/tiles/orders/components/OrderRow.tsx';
import { FixingContext, useValidateFixing } from '@/App/tiles/orders/FixingOrder/FixingContext.tsx';
import { OmsFieldErrorWrapper } from '@/components/ui/OmsFieldErrorWrapper.tsx';
import { useAppSelector } from '@/store/hooks.ts';
import {
  selectFixingErrors,
  selectFixingIsCcyForcedBidAsk,
  selectOrderEntryByIdFromBlotter,
  selectTradeIdFromFixingTile,
} from '@/store/orders/fixing/selectors.ts';

type FixingPriceTypeProps = {
  priceType: 'MID' | 'BIDASK' | undefined;
  setPriceType: (value: 'MID' | 'BIDASK' | undefined) => void;
  setMarginType: (value: 'BidAskMidSpread' | 'MarginInBps' | undefined) => void;
};

export const FixingEditingPriceType = (props: FixingPriceTypeProps) => {
  const { priceType, setPriceType, setMarginType } = props;
  const tileId = useContext(FixingContext).tileId;
  const isCcyForcedBidAsk = useAppSelector((state) => selectFixingIsCcyForcedBidAsk(state, tileId));
  const track = useTrackEvent();

  const tradeId = useAppSelector((state) => selectTradeIdFromFixingTile(state, tileId));
  const orderFromBlotter = useAppSelector((state) =>
    tradeId ? selectOrderEntryByIdFromBlotter(state, tradeId) : undefined,
  );
  const fixingPriceType =
    priceType ?? (isDefined(orderFromBlotter?.fixingPriceType) ? orderFromBlotter?.fixingPriceType : undefined);
  const errors = useAppSelector((state) => selectFixingErrors(state, tileId));

  const PriceTypeError = errors?.fixingPriceType;

  const validateFixing = useValidateFixing();

  const handlePriceTypeChange = async (event: ChangeEvent<HTMLSelectElement>) => {
    track('Order action', 'Price type changed');
    const newPriceType = event.target.value as 'MID' | 'BIDASK';
    if (isDefined(orderFromBlotter)) {
      const [currency1, currency2] = isDefined(orderFromBlotter?.currencyPair)
        ? orderFromBlotter.currencyPair.split('/')
        : ['', ''];

      if (isNotDefined(currency1) || isNotDefined(currency2)) {
        console.error(`[handlePriceTypeChange] Invalid currency pair: ${currency1}/${currency2}`);
        return;
      }

      const response = await validateFixing({
        fixingPriceType: newPriceType,
        fixingMarginType: newPriceType === 'BIDASK' ? 'MarginInBps' : undefined,
      });

      if (newPriceType === 'BIDASK') {
        setMarginType('MarginInBps');
      }

      if (response?.data?.validationResponseDetailContent.length === 0) {
        setPriceType(newPriceType);
      }
    }
  };

  return (
    <OrderRow labelId="order.fields.priceType">
      <OmsFieldErrorWrapper errors={PriceTypeError}>
        <Form.Select value={fixingPriceType} onChange={handlePriceTypeChange} size="sm" data-e2e="price-type-selector">
          {!isCcyForcedBidAsk && (
            <option key="MID" value="MID">
              <FormattedMessage id="order.form.priceType.MID" />
            </option>
          )}
          <option key="BIDASK" value="BIDASK">
            <FormattedMessage id="order.form.priceType.BIDASK" />
          </option>
        </Form.Select>
      </OmsFieldErrorWrapper>
    </OrderRow>
  );
};
